import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { CheckCircle2, AlertCircle, Store } from "lucide-react";

function authHeaders() {
  const token = localStorage.getItem("ayrshire_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

type InviteState =
  | { phase: "loading" }
  | { phase: "not-logged-in" }
  | { phase: "ready"; storeName?: string; role?: string }
  | { phase: "accepting" }
  | { phase: "done"; storeName: string; storeId: number }
  | { phase: "error"; message: string };

export default function AcceptStoreInvite() {
  const { user } = useAuth();
  const [, navigate] = useLocation();
  const token = new URLSearchParams(window.location.search).get("token") ?? "";
  const [state, setState] = useState<InviteState>({ phase: "loading" });

  useEffect(() => {
    if (!token) { setState({ phase: "error", message: "Invalid invite link — no token found." }); return; }
    if (!user) { setState({ phase: "not-logged-in" }); return; }
    setState({ phase: "ready" });
  }, [token, user]);

  async function accept() {
    setState({ phase: "accepting" });
    try {
      const res = await fetch("/api/order/staff/accept-invite", {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ token }),
      });
      const d = await res.json() as { error?: string; storeName?: string; storeId?: number };
      if (!res.ok) { setState({ phase: "error", message: d.error ?? "Failed to accept invite." }); return; }
      setState({ phase: "done", storeName: d.storeName ?? "the store", storeId: d.storeId ?? 0 });
    } catch {
      setState({ phase: "error", message: "Something went wrong. Please try again." });
    }
  }

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle size={40} className="mx-auto mb-3 text-red-400" />
          <p className="text-slate-600">Invalid invite link.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-slate-50">
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-8 max-w-md w-full text-center space-y-5">
        <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto">
          <Store size={28} className="text-primary" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-foreground mb-1">Store Staff Invite</h1>
          <p className="text-sm text-slate-500">You've been invited to manage a store on Ayrshire Hub.</p>
        </div>

        {state.phase === "loading" && (
          <div className="animate-pulse h-10 bg-slate-100 rounded-lg" />
        )}

        {state.phase === "not-logged-in" && (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              You need to be logged in to accept this invite. Log in or create a free account, then come back to this link.
            </p>
            <Button
              className="w-full"
              onClick={() => navigate(`/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`)}
            >
              Log in to accept
            </Button>
            <Button variant="outline" className="w-full"
              onClick={() => navigate(`/signup?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`)}
            >
              Create a free account
            </Button>
          </div>
        )}

        {state.phase === "ready" && (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Click below to accept the invite and gain access to the store portal.
            </p>
            <Button className="w-full" onClick={() => void accept()}>
              Accept Invite
            </Button>
          </div>
        )}

        {state.phase === "accepting" && (
          <div className="space-y-3">
            <div className="animate-pulse h-10 bg-slate-100 rounded-lg" />
            <p className="text-sm text-slate-400">Accepting invite…</p>
          </div>
        )}

        {state.phase === "done" && (
          <div className="space-y-4">
            <CheckCircle2 size={40} className="mx-auto text-emerald-500" />
            <div>
              <p className="font-semibold text-slate-800">Invite accepted!</p>
              <p className="text-sm text-slate-500 mt-1">You now have access to <strong>{state.storeName}</strong>.</p>
            </div>
            <Button className="w-full" onClick={() => navigate("/business/order-store")}>
              Go to Store Portal
            </Button>
          </div>
        )}

        {state.phase === "error" && (
          <div className="space-y-3">
            <AlertCircle size={32} className="mx-auto text-red-400" />
            <p className="text-sm text-red-600">{state.message}</p>
            <Button variant="outline" className="w-full" onClick={() => navigate("/")}>Back to home</Button>
          </div>
        )}
      </div>
    </div>
  );
}
